/**
 * Teakmöbler Theme JavaScript
 * Scandinavian Furniture Store
 */

(function() {
  'use strict';

  // ===========================================
  // Utility Functions
  // ===========================================
  
  const debounce = (fn, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(this, args), delay);
    };
  };

  const trapFocus = (element) => {
    const focusableElements = element.querySelectorAll(
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    element.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey && document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        } else if (!e.shiftKey && document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    });
  };

  // ===========================================
  // Header
  // ===========================================
  
  const initHeader = () => {
    const header = document.querySelector('[data-header]');
    if (!header) return;

    let lastScroll = 0;
    const headerHeight = header.offsetHeight;

    const handleScroll = () => {
      const currentScroll = window.pageYOffset;
      
      if (currentScroll <= 0) {
        header.classList.remove('header--hidden', 'header--scrolled');
        return;
      }

      if (currentScroll > headerHeight) {
        header.classList.add('header--scrolled');
      } else {
        header.classList.remove('header--scrolled');
      }

      if (currentScroll > lastScroll && currentScroll > headerHeight * 2) {
        header.classList.add('header--hidden');
      } else {
        header.classList.remove('header--hidden');
      }

      lastScroll = currentScroll;
    };

    window.addEventListener('scroll', debounce(handleScroll, 10));
  };

  // ===========================================
  // Mobile Menu
  // ===========================================
  
  const initMobileMenu = () => {
    const menuToggle = document.querySelector('[data-menu-toggle]');
    const menuClose = document.querySelector('[data-menu-close]');
    const mobileMenu = document.querySelector('[data-mobile-menu]');
    
    if (!menuToggle || !mobileMenu) return;

    const openMenu = () => {
      mobileMenu.classList.add('is-open');
      mobileMenu.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      trapFocus(mobileMenu);
    };

    const closeMenu = () => {
      mobileMenu.classList.remove('is-open');
      mobileMenu.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    };

    menuToggle.addEventListener('click', openMenu);
    menuClose?.addEventListener('click', closeMenu);

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && mobileMenu.classList.contains('is-open')) {
        closeMenu();
      }
    });
  };

  // ===========================================
  // Cart Drawer
  // ===========================================
  
  const initCartDrawer = () => {
    const cartToggle = document.querySelector('[data-cart-toggle]');
    const cartDrawer = document.querySelector('[data-cart-drawer]');
    const cartOverlay = document.querySelector('[data-cart-overlay]');
    const cartClose = document.querySelectorAll('[data-cart-close]');
    
    if (!cartToggle || !cartDrawer) return;

    const openCart = () => {
      cartDrawer.classList.add('is-open');
      cartDrawer.setAttribute('aria-hidden', 'false');
      cartOverlay?.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
      trapFocus(cartDrawer);
    };

    const closeCart = () => {
      cartDrawer.classList.remove('is-open');
      cartDrawer.setAttribute('aria-hidden', 'true');
      cartOverlay?.classList.remove('is-visible');
      document.body.style.overflow = '';
    };

    cartToggle.addEventListener('click', openCart);
    cartOverlay?.addEventListener('click', closeCart);
    cartClose.forEach(btn => btn.addEventListener('click', closeCart));

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && cartDrawer.classList.contains('is-open')) {
        closeCart();
      }
    });

    // Cart quantity updates
    initCartQuantityButtons();
  };

  const initCartQuantityButtons = () => {
    document.querySelectorAll('[data-increase], [data-decrease], [data-remove]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const key = btn.dataset.increase || btn.dataset.decrease || btn.dataset.remove;
        let quantity;

        if (btn.dataset.remove) {
          quantity = 0;
        } else if (btn.dataset.increase) {
          const item = document.querySelector(`[data-cart-item="${key}"]`);
          const currentQty = parseInt(item.querySelector('span').textContent);
          quantity = currentQty + 1;
        } else {
          const item = document.querySelector(`[data-cart-item="${key}"]`);
          const currentQty = parseInt(item.querySelector('span').textContent);
          quantity = Math.max(0, currentQty - 1);
        }

        await updateCartItem(key, quantity);
      });
    });
  };

  const updateCartItem = async (key, quantity) => {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id: key, quantity })
      });

      if (response.ok) {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error updating cart:', error);
    }
  };

  // ===========================================
  // Product Page
  // ===========================================
  
  const initProductPage = () => {
    const productForm = document.querySelector('[data-product-form]');
    if (!productForm) return;

    // Thumbnail gallery
    initProductGallery();
    
    // Variant selection
    initVariantSelection();
    
    // Quantity selector
    initQuantitySelector();
    
    // Accordion
    initAccordion();
  };

  const initProductGallery = () => {
    const thumbnails = document.querySelectorAll('[data-thumbnail]');
    const mainImage = document.getElementById('main-product-image');
    
    if (!thumbnails.length || !mainImage) return;

    thumbnails.forEach(thumb => {
      thumb.addEventListener('click', () => {
        thumbnails.forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
        mainImage.src = thumb.dataset.src;
      });
    });
  };

  const initVariantSelection = () => {
    const productJson = document.querySelector('[data-product-json]');
    if (!productJson) return;

    const product = JSON.parse(productJson.textContent);
    const variantButtons = document.querySelectorAll('[data-option-value]');
    const variantInput = document.querySelector('[data-variant-id]');
    const priceEl = document.querySelector('[data-product-price]');
    const addToCartBtn = document.querySelector('[data-add-to-cart]');
    const skuEl = document.querySelector('[data-product-sku]');

    let selectedOptions = product.options.map((option, index) => {
      const selectedBtn = document.querySelector(`[data-option-index="${index}"].is-selected`);
      return selectedBtn ? selectedBtn.dataset.optionValue : null;
    });

    variantButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const optionIndex = parseInt(btn.dataset.optionIndex);
        const value = btn.dataset.optionValue;
        
        // Update selected state
        document.querySelectorAll(`[data-option-index="${optionIndex}"]`).forEach(b => {
          b.classList.remove('is-selected');
        });
        btn.classList.add('is-selected');
        
        // Update selected options
        selectedOptions[optionIndex] = value;
        
        // Find matching variant
        const variant = product.variants.find(v => {
          return selectedOptions.every((opt, i) => v.options[i] === opt);
        });

        if (variant) {
          variantInput.value = variant.id;
          
          // Update price
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            priceEl.innerHTML = `<s>${formatMoney(variant.compare_at_price)}</s> <ins>${formatMoney(variant.price)}</ins>`;
          } else {
            priceEl.textContent = formatMoney(variant.price);
          }

          // Update availability
          if (variant.available) {
            addToCartBtn.disabled = false;
            addToCartBtn.textContent = 'Add to Cart';
          } else {
            addToCartBtn.disabled = true;
            addToCartBtn.textContent = 'Sold Out';
          }

          // Update SKU
          if (skuEl && variant.sku) {
            skuEl.textContent = variant.sku;
          }

          // Update URL
          const url = new URL(window.location);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url);
        }
      });
    });
  };

  const formatMoney = (cents) => {
    const amount = (cents / 100).toFixed(2);
    return `${amount} kr`; // Swedish krona
  };

  const initQuantitySelector = () => {
    const decreaseBtn = document.querySelector('[data-decrease-qty]');
    const increaseBtn = document.querySelector('[data-increase-qty]');
    const quantityInput = document.querySelector('[data-quantity-input]');
    
    if (!decreaseBtn || !increaseBtn || !quantityInput) return;

    decreaseBtn.addEventListener('click', () => {
      const current = parseInt(quantityInput.value);
      if (current > 1) {
        quantityInput.value = current - 1;
      }
    });

    increaseBtn.addEventListener('click', () => {
      const current = parseInt(quantityInput.value);
      quantityInput.value = current + 1;
    });
  };

  const initAccordion = () => {
    const triggers = document.querySelectorAll('.accordion__trigger');
    
    triggers.forEach(trigger => {
      trigger.addEventListener('click', () => {
        const item = trigger.closest('.accordion__item');
        const isOpen = item.classList.contains('is-open');
        
        // Close all
        document.querySelectorAll('.accordion__item').forEach(i => {
          i.classList.remove('is-open');
          i.querySelector('.accordion__trigger').setAttribute('aria-expanded', 'false');
        });
        
        // Open clicked if it was closed
        if (!isOpen) {
          item.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
        }
      });
    });
  };

  // ===========================================
  // Add to Cart
  // ===========================================
  
  const initAddToCart = () => {
    document.querySelectorAll('form[action="/cart/add"]').forEach(form => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        
        const submitBtn = form.querySelector('[type="submit"]');
        const originalText = submitBtn.textContent;
        submitBtn.textContent = 'Adding...';
        submitBtn.disabled = true;

        try {
          const formData = new FormData(form);
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData
          });

          if (response.ok) {
            submitBtn.textContent = 'Added!';
            
            // Update cart count
            const cart = await fetch('/cart.js').then(r => r.json());
            const countEl = document.querySelector('[data-cart-count]');
            if (countEl) {
              countEl.textContent = cart.item_count;
            }

            // Open cart drawer
            const cartDrawer = document.querySelector('[data-cart-drawer]');
            if (cartDrawer) {
              setTimeout(() => {
                window.location.reload();
              }, 500);
            }
          } else {
            throw new Error('Failed to add to cart');
          }
        } catch (error) {
          console.error('Error adding to cart:', error);
          submitBtn.textContent = 'Error';
        }

        setTimeout(() => {
          submitBtn.textContent = originalText;
          submitBtn.disabled = false;
        }, 2000);
      });
    });
  };

  // ===========================================
  // Animations
  // ===========================================
  
  const initAnimations = () => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fade-in');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    document.querySelectorAll('.product-card, .split-content, .testimonial').forEach(el => {
      el.style.opacity = '0';
      observer.observe(el);
    });
  };

  // ===========================================
  // Initialize
  // ===========================================
  
  document.addEventListener('DOMContentLoaded', () => {
    initHeader();
    initMobileMenu();
    initCartDrawer();
    initProductPage();
    initAddToCart();
    initAnimations();
  });

})();
